<script setup lang="ts">
/**
 * Resignation of Secretary — MVP SecretaryResignationDoc 이식.
 * Studio 챗봇(ConversationPanel)과 CddDocVarField 연동.
 * Signers: package SignersField UI + 기존 Add Signers 모달(SignerListField).
 * 정관번호(constitution_clause_number)는 문서에 유지.
 * MVP 와 달리 DCR/Letter 리스트에는 NRIC/Passport 국적 라디오가 없고, license_type / license_number / ssm_pc_no 를 노출.
 */
import { ref, reactive, computed, watch } from 'vue';
import CddDocVarField from '../cdd/CddDocVarField.vue';
import PagedDoc from './PagedDoc.vue';
import SignersField from './signers/SignersField.vue';
import type { SignerEntry } from '@/features/AI-documents/types/docGen.types';
import { REASONS, type DirectorMode, type ResolutionBasis, type ResignReason } from './resignSecretaryFields';

const props = defineProps<{
  /** Signers / director-count 조회용 entityCd */
  entityCd?: string;
}>();

const rows = ref<number[]>([1]);
let seq = 1;
const reasons = reactive<Record<number, ResignReason>>({ 1: 'Resignation' });
const directorMode = ref<DirectorMode>('sole');
const basis = ref<ResolutionBasis>('constitution');
const showClause = computed(() => basis.value !== 'third_schedule');
const signers = ref<SignerEntry[]>([]);
const signers58 = reactive<Record<number, SignerEntry[]>>({});
/** Resignation Letter 문서별 싸이너 (사임 비서 본인) — UI 없이 prepare 시 사용 */
const letterSigners = ref<Record<number, SignerEntry[]>>({});

const multiple = computed(() => rows.value.length > 1);

function addRow() {
  seq += 1;
  rows.value.push(seq);
  reasons[seq] = 'Resignation';
}
function removeRow(id: number) {
  if (rows.value.length <= 1) return;
  rows.value = rows.value.filter((r) => r !== id);
  delete reasons[id];
  delete signers58[id];
}

function onBoardSignersUpdate(list: SignerEntry[]) {
  signers.value = Array.isArray(list) ? list.map((s) => ({ ...s })) : [];
}
function on58SignersUpdate(id: number, list: SignerEntry[]) {
  signers58[id] = Array.isArray(list) ? list.map((s) => ({ ...s })) : [];
}

function setDirectorMode(mode: DirectorMode) {
  directorMode.value = mode === 'multiple' ? 'multiple' : 'sole';
}

/**
 * 문서별 싸이너 자동 지정 (서로 독립 배열).
 *  - DCR: 전체 이사 (사임 비서는 이사가 아니므로 제외 대상 없음)
 *  - Letter: 사임 비서 본인 (rows 별 letterSigners)
 *  - 58I: 해임 비서를 제외한 나머지 비서
 */
function applyAutoSigners(input: {
  board?: SignerEntry[];
  secretariesByRow?: Record<number, SignerEntry[]>;
  letterByRow?: Record<number, SignerEntry[]>;
}) {
  if (Array.isArray(input.board)) {
    signers.value = input.board.map((s) => ({ ...s }));
  }
  if (input.secretariesByRow) {
    for (const [k, v] of Object.entries(input.secretariesByRow)) {
      signers58[Number(k)] = (v || []).map((s) => ({ ...s }));
    }
  }
  if (input.letterByRow) {
    letterSigners.value = Object.fromEntries(
      Object.entries(input.letterByRow).map(([k, v]) => [
        Number(k),
        (v || []).map((s) => ({ ...s })),
      ]),
    );
  }
}

/** entity 이사 수로 Sole / Multiple 자동 설정 */
async function syncDirectorModeFromEntity(cd?: string) {
  const code = String(cd || props.entityCd || '').trim();
  if (!code) return;
  try {
    const res = await fetch(`/api/documents/variables/directors/${encodeURIComponent(code)}`, {
      credentials: 'include',
    }).then((r) => r.json());
    const directors: any[] = res?.data?.directors || res?.directors || [];
    const active = directors.filter((d) => {
      const ceased = String(d.cessationDate || d.CESSATION_DATE || d.dateOfCessation || '').trim();
      return !ceased;
    });
    const n = active.length || directors.length;
    if (n > 0) setDirectorMode(n === 1 ? 'sole' : 'multiple');
  } catch (err) {
    console.warn('[Resignation] director count fetch failed:', err);
  }
}

watch(
  () => props.entityCd,
  (cd) => { if (cd) void syncDirectorModeFromEntity(cd); },
  { immediate: true },
);

function gatherLocalState() {
  return {
    rows: [...rows.value],
    reasons: { ...reasons },
    directorMode: directorMode.value,
    basis: basis.value,
    signers: signers.value.map((s) => ({ ...s })),
    signers58: Object.fromEntries(
      Object.entries(signers58).map(([k, v]) => [k, (v || []).map((s) => ({ ...s }))]),
    ),
    letterSigners: Object.fromEntries(
      Object.entries(letterSigners.value).map(([k, v]) => [k, (v || []).map((s) => ({ ...s }))]),
    ),
  };
}

function restoreLocalState(s: any) {
  if (!s) return;
  if (Array.isArray(s.rows) && s.rows.length) {
    rows.value = [...s.rows];
    seq = Math.max(...s.rows, seq);
  }
  if (s.reasons) Object.assign(reasons, s.reasons);
  if (s.directorMode) directorMode.value = s.directorMode;
  if (s.basis) basis.value = s.basis;
  if (Array.isArray(s.signers)) signers.value = s.signers.map((x: SignerEntry) => ({ ...x }));
  if (s.signers58) {
    for (const [k, v] of Object.entries(s.signers58)) {
      signers58[Number(k)] = Array.isArray(v) ? (v as SignerEntry[]).map((x) => ({ ...x })) : [];
    }
  }
  if (s.letterSigners) {
    letterSigners.value = Object.fromEntries(
      Object.entries(s.letterSigners).map(([k, v]) => [
        Number(k),
        Array.isArray(v) ? (v as SignerEntry[]).map((x) => ({ ...x })) : [],
      ]),
    );
  }
}

defineExpose({
  gatherLocalState,
  restoreLocalState,
  rows,
  setDirectorMode,
  syncDirectorModeFromEntity,
  applyAutoSigners,
  setBasis: (b: ResolutionBasis) => {
    if (b === 'constitution' || b === 'articles' || b === 'third_schedule') {
      basis.value = b;
    }
  },
  /** Studio 진행률·basis 동기화용 */
  basis,
});
</script>

<template>
  <div class="rsn-sheets">
    <!-- Document 1 · Directors' Circular Resolution -->
    <div class="rsn-doc">
      <div class="rsn-doclabel">
        <span class="ic">📄</span>
        <span class="nm">Directors' Circular Resolution</span>
        <span class="n">Document 1 / 3</span>
      </div>
      <PagedDoc>
        <div class="rsn-center">
          <div class="rsn-co"><CddDocVarField field-key="company_name" /></div>
          <div class="rsn-sub">(Incorporated in Malaysia)</div>
        </div>

        <h2 class="rsn-h">DIRECTORS' CIRCULAR RESOLUTION</h2>

        <div class="rsn-pick">
          <span class="cdd-cbx"><span class="box" @click="directorMode = 'sole'">{{ directorMode === 'sole' ? '✓' : '' }}</span>Sole director</span>
          <span class="cdd-cbx"><span class="box" @click="directorMode = 'multiple'">{{ directorMode === 'multiple' ? '✓' : '' }}</span>Multiple directors</span>
        </div>

        <p class="rsn-p">
          <template v-if="directorMode === 'sole'">I, the undersigned, being the sole director</template>
          <template v-else>We, the undersigned, being the directors</template>
          of the above-mentioned Company for the time being, do resolve the following in writing pursuant to
          <template v-if="basis === 'constitution'">Clause <CddDocVarField field-key="constitution_clause_number" /> of the Company's Constitution</template>
          <template v-else-if="basis === 'articles'">Article <CddDocVarField field-key="constitution_clause_number" /> of the Articles of Association</template>
          <template v-else>Paragraph 15 of the Third Schedule of the Companies Act 2016</template>.
        </p>

        <div class="rsn-pick sm">
          <span class="cdd-cbx"><span class="box" @click="basis = 'constitution'">{{ basis === 'constitution' ? '✓' : '' }}</span>Constitution (Clause)</span>
          <span class="cdd-cbx"><span class="box" @click="basis = 'articles'">{{ basis === 'articles' ? '✓' : '' }}</span>Articles of Association</span>
          <span class="cdd-cbx"><span class="box" @click="basis = 'third_schedule'">{{ basis === 'third_schedule' ? '✓' : '' }}</span>Third Schedule, para 15</span>
          <span v-if="showClause" class="rsn-clause">Clause / Article No.: <CddDocVarField field-key="constitution_clause_number" /></span>
        </div>

        <h3 class="rsn-h3">RESIGNATION OF SECRETAR{{ multiple ? 'IES' : 'Y' }}</h3>
        <p class="rsn-p">
          THAT the resignation of the following secretar{{ multiple ? 'ies' : 'y' }} of the Company be and
          {{ multiple ? 'are' : 'is' }} hereby accepted with immediate effect:-
        </p>

        <table class="cdd-tbl rsn-list">
          <tbody>
            <tr class="rsn-list-h">
              <th style="width:34px">#</th>
              <th>Secretary</th>
              <th style="width:34px"></th>
            </tr>
            <tr v-for="(id, i) in rows" :key="id">
              <td class="rsn-idx">{{ i + 1 }}</td>
              <td>
                <div class="rsn-line">
                  <CddDocVarField :field-key="`sec${id}_full_name`" />
                  <span class="rsn-paren">(<CddDocVarField :field-key="`sec${id}_license_type`" /><CddDocVarField :field-key="`sec${id}_license_number`" />)</span>
                </div>
                <div class="rsn-line dim">(SSM PC No.: <CddDocVarField :field-key="`sec${id}_ssm_pc_no`" />)</div>
              </td>
              <td class="rsn-rm">
                <button v-if="rows.length > 1" class="rsn-x" title="Remove" @click="removeRow(id)">✕</button>
              </td>
            </tr>
            <tr class="rsn-addrow">
              <td colspan="3">
                <button class="rsn-add" @click="addRow"><span class="rsn-add-ic">＋</span> Add secretary</button>
              </td>
            </tr>
          </tbody>
        </table>

        <p class="rsn-p">
          AND THAT the Secretary be authorized and instructed to forward and submit the resignation herein
          approved by the Board to the Companies Commission of Malaysia and to update the Register of Directors,
          Managers and Secretaries accordingly in that respect.
        </p>

        <div class="rsn-signers">
          <SignersField
            :key="'dcr-signers'"
            :model-value="signers"
            board-label="BOARD OF DIRECTORS"
            var-key="signers_dcr"
            :entity-cd="entityCd || ''"
            @update:model-value="onBoardSignersUpdate"
          />
        </div>
        <div class="rsn-date">Date: <CddDocVarField field-key="resolution_date" /></div>
      </PagedDoc>
    </div>

    <!-- Document 2 · Resignation Letter (per secretary) -->
    <div v-for="(id, i) in rows" :key="`ltr-${id}`" class="rsn-doc">
      <div class="rsn-doclabel">
        <span class="ic">✉️</span>
        <span class="nm">Secretary Resignation Letter<span v-if="multiple" class="sub"> · {{ i + 1 }} of {{ rows.length }}</span></span>
        <span class="n">Document 2 / 3</span>
      </div>
      <PagedDoc>
        <div class="rsn-letterhead">
          <div><CddDocVarField :field-key="`sec${id}_full_name`" /></div>
          <div><CddDocVarField :field-key="`sec${id}_residential_address`" /></div>
        </div>
        <div class="rsn-date">Date: <CddDocVarField field-key="letter_date" /></div>

        <div class="rsn-to">
          <div>To The Board of Directors</div>
          <div><CddDocVarField field-key="company_name" /></div>
          <div>Company Registration No. <CddDocVarField field-key="company_registration_no" /></div>
          <div><CddDocVarField field-key="company_address" /></div>
        </div>

        <p class="rsn-p">Dear Sir/Mdm,</p>
        <p class="rsn-p bold">RESIGNATION AS SECRETARY OF THE COMPANY</p>
        <p class="rsn-p">I hereby tender my resignation as Company Secretary of the Company with immediate effect.</p>
        <p class="rsn-p">Thank you.</p>

        <div class="rsn-yours">
          <div>Yours faithfully,</div>
          <div class="rsn-sign-line">______________________________</div>
          <div><CddDocVarField :field-key="`sec${id}_full_name`" /></div>
          <div>NRIC/Passport No. <CddDocVarField :field-key="`sec${id}_ic_passport_no`" /></div>
        </div>
      </PagedDoc>
    </div>

    <!-- Document 3 · Section 58I (per secretary) -->
    <div v-for="(id, i) in rows" :key="`s58-${id}`" class="rsn-doc">
      <div class="rsn-doclabel">
        <span class="ic">📋</span>
        <span class="nm">Section 58I — Cessation of Secretary<span v-if="multiple" class="sub"> · {{ i + 1 }} of {{ rows.length }}</span></span>
        <span class="n">Document 3 / 3</span>
      </div>
      <PagedDoc>
        <div class="rsn-center">
          <div class="rsn-act">COMPANIES ACT 2016</div>
          <div class="rsn-act sm">Section 58</div>
        </div>
        <h3 class="rsn-h3 center">NOTIFICATION OF CHANGE IN THE REGISTER OF DIRECTORS, MANAGERS AND SECRETARIES</h3>
        <div class="rsn-center"><div class="rsn-co"><CddDocVarField field-key="company_name" /></div></div>

        <div class="rsn-secbar">NATURE OF CHANGE</div>
        <div class="rsn-natlist">
          <div><span class="cbx off">☐</span> Change in the particulars of a director (Section A)</div>
          <div><span class="cbx off">☐</span> New director (Section B)</div>
          <div><span class="cbx off">☐</span> Cessation as a director (Section C)</div>
          <div><span class="cbx off">☐</span> Change in the particular of manager (Section D)</div>
          <div><span class="cbx off">☐</span> New Manager (Section E)</div>
          <div><span class="cbx off">☐</span> Cessation as a manager (Section F)</div>
          <div><span class="cbx off">☐</span> Change in the particular of a secretary (Section G)</div>
          <div><span class="cbx off">☐</span> New Secretary (Section H)</div>
          <div class="on"><span class="cbx">☒</span> Cessation as a secretary (Section I)</div>
        </div>

        <div class="rsn-secbar">Section I: CESSATION OF SECRETARY</div>
        <table class="cdd-tbl">
          <tbody>
            <tr><td class="cdd-lcell">Name</td><td><CddDocVarField :field-key="`sec${id}_full_name`" /></td></tr>
            <tr><td class="cdd-lcell">NRIC / Passport No.</td><td><CddDocVarField :field-key="`sec${id}_ic_passport_no`" /></td></tr>
            <tr><td class="cdd-lcell">Date of appointment</td><td><CddDocVarField :field-key="`sec${id}_date_of_appointment`" /></td></tr>
            <tr><td class="cdd-lcell">Date of cessation</td><td><CddDocVarField :field-key="`sec${id}_date_of_cessation`" /></td></tr>
            <tr>
              <td class="cdd-lcell">Reason of cessation</td>
              <td>
                <span v-for="r in REASONS" :key="r" class="cdd-cbx">
                  <span class="box" @click="reasons[id] = r">{{ reasons[id] === r ? '✓' : '' }}</span>{{ r }}
                </span>
              </td>
            </tr>
          </tbody>
        </table>

        <div class="rsn-decl">
          <div>Declaration:</div>
          <p class="rsn-p">I confirm that the facts and information stated in this document are true and to the best of my knowledge.</p>
          <div>Signed:</div>
          <SignersField
            :key="'s58-signers-' + id"
            :model-value="signers58[id] || []"
            :var-key="'signers58_' + id"
            :entity-cd="entityCd || ''"
            @update:model-value="(list) => on58SignersUpdate(id, list)"
          />
        </div>
      </PagedDoc>
    </div>
  </div>
</template>

<style scoped>
.rsn-sheets { color:#111; --a4-w:794px; --a4-h:1123px; --a4-margin:96px; position:relative; }
.rsn-doc { width:var(--a4-w); max-width:100%; margin-left:auto; margin-right:auto; }
.rsn-doc + .rsn-doc { margin-top:34px; }
.rsn-doclabel { display:flex; align-items:center; gap:7px; padding:0 4px 9px; }
.rsn-doclabel .ic { font-size:14px; line-height:1; }
.rsn-doclabel .nm { font-size:12px; font-weight:800; color:#42506a; letter-spacing:.01em; }
.rsn-doclabel .sub { font-weight:700; color:#8a97b5; }
.rsn-doclabel .n { margin-left:auto; font-size:10.5px; font-weight:800; color:#9aa6bd; letter-spacing:.02em; white-space:nowrap; }

.rsn-center { text-align:center; margin:10px 0; }
.rsn-co { font-weight:800; font-size:15px; }
.rsn-sub { font-size:12px; margin-top:2px; }
.rsn-h { text-align:center; font-weight:800; font-size:15px; letter-spacing:.02em; margin:18px 0 14px; text-decoration:underline; }
.rsn-h3 { font-weight:800; font-size:13.5px; margin:18px 0 8px; text-decoration:underline; }
.rsn-h3.center { text-align:center; text-decoration:none; }
.rsn-p { font-size:12.5px; line-height:1.7; margin:8px 0; }
.rsn-p.bold, .bold { font-weight:800; }
.rsn-pick { display:flex; flex-wrap:wrap; gap:6px 18px; margin:10px 0; align-items:center; }
.rsn-pick.sm { font-size:11.5px; }
.rsn-clause { font-size:11.5px; color:#42506a; margin-left:4px; }

.rsn-list { margin:12px 0 6px; }
.rsn-list-h th { background:#f2f5fa; font-weight:800; font-size:11.5px; color:#42506a; }
.rsn-idx { text-align:center; font-weight:700; color:#5b6472; vertical-align:middle; }
.rsn-line { font-size:12.5px; line-height:1.8; }
.rsn-line.dim { color:#42506a; }
.rsn-paren { margin-left:4px; }
.rsn-rm { text-align:center; vertical-align:middle; }
.rsn-x { border:none; background:#fdecec; color:#d64545; width:22px; height:22px; border-radius:6px; font-size:11px; cursor:pointer; font-weight:700; }
.rsn-x:hover { background:#f9d5d5; }
.rsn-addrow td { padding:0 !important; border:1px solid #000; border-top:none; }
.rsn-add { width:100%; display:flex; align-items:center; justify-content:center; gap:6px; background:#eef4ff; border:none; color:#1B64DA; font-size:12.5px; font-weight:800; padding:9px; cursor:pointer; transition:background .15s; }
.rsn-add:hover { background:#dfeaff; }
.rsn-add-ic { font-size:15px; line-height:1; }

.rsn-signers { margin:0; }
.rsn-sign-line { letter-spacing:1px; color:#333; }
.rsn-date { font-size:12.5px; font-weight:700; margin-top:14px; }
.rsn-letterhead { font-size:12.5px; line-height:1.8; margin-bottom:14px; }
.rsn-to { font-size:12.5px; line-height:1.8; margin:14px 0; }
.rsn-yours { margin-top:28px; font-size:12.5px; line-height:2; }
.rsn-yours .rsn-sign-line { margin:22px 0 6px; }

.rsn-act { font-weight:800; font-size:14px; }
.rsn-act.sm { font-size:12px; font-weight:700; }
.rsn-secbar { background:#e9edf4; font-weight:800; font-size:12px; padding:6px 10px; margin:16px 0 0; border:1px solid #000; }
.rsn-natlist { border:1px solid #000; border-top:none; padding:8px 10px; font-size:11.5px; line-height:1.9; }
.rsn-natlist .on { font-weight:800; }
.rsn-natlist .cbx { display:inline-block; width:16px; }
.rsn-natlist .cbx.off { color:#888; }
.rsn-decl { margin-top:16px; font-size:12px; line-height:1.7; }
</style>
